import { useMemo, type MouseEvent } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { Heading, ButtonGroup, Paragraph, Modal } from '~elements'
import styles from './CardOverlay.module.scss'
import { ButtonProps, CardOverlayProps } from '~types'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { getImageUrl } from 'utils/assets/getImageUrl'
import { useWindowSize } from 'utils/hooks'
import Video from '../Video/Video'
import cx from 'classnames'
import { useModal } from '~utils/hooks/useModal'
import { useModalExists } from 'lib/context/modalContext'

const CardOverlay = ({
  className,
  backgroundImage,
  backgroundVideo,
  contentClassname,
  ctas,
  description,
  hasVideo,
  headline,
  headlineLogo,
  subheadline,
  subheadlineColor = 'primary',
  url,
  onCardClick,
  isCarouselSlide = false,
  hideContent = false,
  showModalOnClick = false
}: CardOverlayProps) => {
  const { isModalOpen, openModal, closeModal } = useModal()
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const { isAnyModalOpen } = useModalExists()
  const linkModule = 'card_overlay'
  const router = useRouter()
  const imageUrl = getImageUrl({ imageUrl: backgroundImage?.url })
  const shouldOpenModalOnClick = showModalOnClick && backgroundVideo

  const ctasWithModals = useMemo(() => {
    return ctas?.map(
      cta =>
        ({
          ...cta,
          ...(cta.openModalOnClick && {
            modal: {
              view: {
                componentName: 'ModalViewMedia',
                mediaVideo: backgroundVideo
              },
              backgroundColor: 'gray-900',
              width: 'content-aware',
              overlayTheme: 'blur'
            },
            url: null
          })
        } as ButtonProps)
    )
  }, [ctas])

  const handleCardClick = (e: MouseEvent<HTMLDivElement>) => {
    const targetEl = e.target as HTMLElement

    // Check if the actual clicked element is a button or link
    const isDirectButtonClick = targetEl.tagName === 'BUTTON' || targetEl.tagName === 'A'
    const isInsideButton = targetEl.closest('button') || targetEl.closest('a')

    // Only return if the click originated directly from a button/link
    // or from inside a button container, OR is inside any button/link
    if (isDirectButtonClick || isInsideButton || targetEl.closest(`.${styles['btn-container']}`)) return

    if (onCardClick) {
      onCardClick(e)
    }

    // If content is hidden (non-centered slide), only allow carousel navigation
    // Don't open modals or navigate to URLs
    if (hideContent) {
      return
    }

    // Only centered slides can open modals or navigate
    if (!isAnyModalOpen && shouldOpenModalOnClick) {
      openModal()
      return
    }

    // Default behavior: navigate to URL
    if (url && !shouldOpenModalOnClick) {
      router.push(url)
    }
  }

  const mainContainerClass = cx(styles['card-overlay'], (showModalOnClick || url) && styles['clickable'], className)
  // Combine default content class with any additional className passed via props
  const contentClass = cx(
    styles['card-overlay-content'],
    contentClassname,
    isCarouselSlide && styles['is-carousel-slide'],
    hideContent && styles['content-hidden']
  )
  return (
    <>
      <div className={mainContainerClass} onClick={handleCardClick}>
        <div className={cx(styles['image-container'], 'card-overlay-image-wrapper')}>
          {hasVideo && backgroundVideo ? (
            <Video
              {...backgroundVideo}
              autoPlay={true}
              videoLoop={true}
              restartOnLeaveViewport={true}
              autoPlayDelay={700}
              isPaused={hideContent}
            />
          ) : (
            imageUrl && (
              <div className={styles['image-wrapper']}>
                <Image
                  alt={backgroundImage?.altText ?? headline?.title ?? ''}
                  src={imageUrl}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={NEXT_IMAGE_QUALITY}
                />
              </div>
            )
          )}
        </div>
        <div className={contentClass}>
          <div className={styles['card-overlay-content__inner']}>
            {headline && !headlineLogo && (
              <Heading level={headline.level} levelDisplay={headline.levelDisplay} noMarginTop noMarginBottom>
                {headline.title}
              </Heading>
            )}
            {headlineLogo && (
              <div className={styles['headline-logo']}>
                <Image
                  alt={headlineLogo.altText || ''}
                  src={headlineLogo.url}
                  width={headlineLogo.width}
                  height={headlineLogo.height}
                  quality={NEXT_IMAGE_QUALITY}
                />
              </div>
            )}
            {subheadline && (
              <Heading level={6} levelDisplay='eyebrow' color={subheadlineColor} noMarginBottom>
                {subheadline}
              </Heading>
            )}

            {description && <Paragraph text={description} noMarginBottom />}
          </div>
          {ctas && ctas.length > 0 && (
            <div className={styles['button-group-container']}>
              <ButtonGroup
                buttons={ctasWithModals}
                analytics={{
                  linkModule: linkModule,
                  parentHeadline: headline?.title
                }}
                layout={isBelowSm && !isCarouselSlide ? 'stacked' : 'inline'}
              />
            </div>
          )}
        </div>
      </div>

      {shouldOpenModalOnClick && isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          handleModalClose={closeModal}
          view={{
            componentName: 'ModalViewMedia',
            mediaVideo: backgroundVideo
          }}
          backgroundColor='gray-900'
          width='content-aware'
          overlayTheme='blur'
        />
      )}
    </>
  )
}

export default CardOverlay
